import type { GigaPrice } from '../types';

/**
 * 获取产品的最佳价格
 * 优先级：专享价 > 折扣价 > 原价
 */
export function getBestPrice(priceInfo: GigaPrice): number {
  if (priceInfo.exclusivePrice != null && priceInfo.exclusivePrice > 0) {
    return priceInfo.exclusivePrice;
  }
  if (priceInfo.discountedPrice != null && priceInfo.discountedPrice > 0) {
    return priceInfo.discountedPrice;
  }
  return priceInfo.price;
}

/**
 * 获取价格类型标签
 */
export function getPriceLabel(priceInfo: GigaPrice): string {
  if (priceInfo.exclusivePrice != null && priceInfo.exclusivePrice > 0) {
    return '专享价';
  }
  if (priceInfo.discountedPrice != null && priceInfo.discountedPrice > 0) {
    return '折扣价';
  }
  return '原价';
}

/**
 * 判断是否有促销
 */
export function hasPromotion(priceInfo: GigaPrice): boolean {
  return !!(priceInfo.discountedPrice && priceInfo.promotionFrom && priceInfo.promotionTo);
}

/**
 * 计算建议售价（基于分销价加成）
 * @param costPrice 分销价（成本价）
 * @param shippingFee 运费
 * @param markup 加价百分比（默认 30%）
 */
export function calculateSuggestedPrice(costPrice: number, shippingFee: number, markup: number = 0.3): number {
  const totalCost = costPrice + (shippingFee || 0);
  return Math.ceil(totalCost * (1 + markup) * 100) / 100;
}

/**
 * 计算利润率
 * @param sellingPrice 销售价
 * @param costPrice 分销价
 * @param shippingFee 运费
 */
export function calculateProfitMargin(sellingPrice: number, costPrice: number, shippingFee: number): number {
  const totalCost = costPrice + (shippingFee || 0);
  if (sellingPrice <= 0) return 0;
  return ((sellingPrice - totalCost) / sellingPrice) * 100;
}

/**
 * 根据成本价、运费和利润率计算销售价
 * @param costPrice 成本价（分销价）
 * @param shippingFee 运费
 * @param profitMarginPercent 利润率百分比（如 30 表示 30%）
 */
export function calculateSellingPrice(costPrice: number, shippingFee: number, profitMarginPercent: number): number {
  const totalCost = costPrice + (shippingFee || 0);
  const sellingPrice = totalCost * (1 + profitMarginPercent / 100);
  return Math.ceil(sellingPrice * 100) / 100; // 向上取整到分
}

/**
 * 格式化价格显示
 */
export function formatPrice(price: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(price);
}

/**
 * 获取运费显示文本
 */
export function getShippingFeeText(priceInfo: GigaPrice): string {
  if (priceInfo.shippingFeeRange) {
    const { minAmount, maxAmount } = priceInfo.shippingFeeRange;
    if (minAmount != null && maxAmount != null && minAmount !== maxAmount) {
      return `${formatPrice(minAmount)} - ${formatPrice(maxAmount)}`;
    }
  }
  if (priceInfo.shippingFee != null) {
    return formatPrice(priceInfo.shippingFee);
  }
  return '待确认';
}
